'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { X } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { Progress } from '@/shared/ui/shadcn/progress';
import { anglesService } from '@/shared/api/services/angles.service';
import type { UploadAngle } from '@/types/upload';
import { isAngleValidationFinished } from '@/shared/constants/status';
import { useUnifiedProgress } from '@/features/angle/hooks/use-unified-progress';
import {
  type ValidationItem,
  type UploadPhase,
  type UploadProgress,
  type PreloadProgress,
  useValidationPolling,
  useValidationListener,
  ValidationItemsList,
  UploadingPhaseContent,
  LoadingPreviewPhaseContent,
  CompletePhaseContent,
  IdlePhaseContent,
} from './upload';

interface UnifiedUploadModalProps {
  isOpen: boolean;
  phase: UploadPhase;
  uploadProgress?: UploadProgress;
  angles: UploadAngle[];
  onClose: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  onComplete?: (validAngles: UploadAngle[]) => void;
}

export function UnifiedUploadModal({
  isOpen,
  phase,
  uploadProgress,
  angles,
  onClose,
  onCancel,
  onRetry,
  onComplete,
}: UnifiedUploadModalProps) {
  const t = useTranslations('angle.upload.modal');
  const tCommon = useTranslations('common');
  const [validationItems, setValidationItems] = useState<ValidationItem[]>([]);
  const [allValidated, setAllValidated] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [showTimeout, setShowTimeout] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [hasCompletedPreview, setHasCompletedPreview] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState<PreloadProgress>({
    loaded: 0,
    total: 0,
  });
  const completedRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Eased timer for the validating phase only — LLM validation emits no
  // progress signal, so a time-based curve is the honest option there. The
  // uploading phase shows real byte progress from `uploadProgress` instead.
  const unifiedProgress = useUnifiedProgress();

  // Validation polling fallback
  const { clearPolling } = useValidationPolling({
    phase,
    validationItems,
    setValidationItems,
    notSuitableImageMessage: t('notSuitableImage'),
    validationTimeoutMessage: t('validationTimeout'),
  });

  // WebSocket listener for validation updates
  useValidationListener({
    setValidationItems,
    notSuitableImageMessage: t('notSuitableImage'),
  });

  // Start the eased timer when validation begins (no progress signal there);
  // uploading shows real progress, so the timer stays off for that phase.
  useEffect(() => {
    if (isOpen && phase === 'validating') {
      unifiedProgress.start();
    } else if (!isOpen || phase === 'idle' || phase === 'uploading') {
      unifiedProgress.reset();
    }
  }, [isOpen, phase, unifiedProgress]);

  // Reset hasCompletedPreview when modal closes or phase changes
  useEffect(() => {
    if (!isOpen || phase === 'idle' || phase === 'uploading') {
      setHasCompletedPreview(false);
    }
  }, [isOpen, phase]);

  // Initialize validation items when phase changes to validating
  useEffect(() => {
    if (phase === 'validating' && angles.length > 0) {
      const items = angles.map((angle) => {
        const validation = angle.attributes?.validation;

        if (validation && isAngleValidationFinished(validation.status)) {
          const isValid = validation.status === 'valid';
          const isTechnicalError = validation.status === 'technical_error';
          const isProhibited = validation.status === 'prohibited';

          return {
            id: angle.id,
            fileName: angle.attributes?.original_filename || 'Image',
            status: (isValid ? 'valid' : 'invalid') as ValidationItem['status'],
            validation: {
              is_valid: isValid,
              is_prohibited: isProhibited,
              is_technical_error: isTechnicalError,
              recommendations: validation.recommendations ?? undefined,
            },
            error: !isValid
              ? validation.recommendations || t('notSuitableImage')
              : undefined,
          };
        }

        return {
          id: angle.id,
          fileName: angle.attributes?.original_filename || 'Image',
          status: 'validating' as ValidationItem['status'],
          validation: undefined,
        };
      });

      setValidationItems(items);
      setAllValidated(false);
      setHasErrors(false);
      setShowTimeout(false);
      completedRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setShowTimeout(true);
      }, 30000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [phase, angles, t]);

  // Check if all items are validated
  useEffect(() => {
    if (phase === 'validating' && validationItems.length > 0) {
      const allDone = validationItems.every(
        (item) => item.status !== 'validating'
      );
      const hasInvalid = validationItems.some(
        (item) =>
          (item.status === 'invalid' && !item.validation?.is_technical_error) ||
          item.status === 'error'
      );

      setAllValidated(allDone);
      setHasErrors(hasInvalid);
    }
  }, [validationItems, phase]);

  // Handle loading preview and completing upload
  const handleLoadPreviewAndComplete = useCallback(async () => {
    if (completedRef.current) return;
    completedRef.current = true;

    try {
      const validAngles = angles.filter((angle) => {
        const validationItem = validationItems.find(
          (item) => item.id === angle.id
        );
        return validationItem?.status === 'valid';
      });

      if (validAngles.length === 0) {
        console.warn('[UnifiedUploadModal] All angles were invalid or deleted');
        unifiedProgress.complete();
        if (onComplete) {
          onComplete([]);
        }
        return;
      }

      setIsLoadingPreview(true);

      const updatedAngles = await Promise.all(
        validAngles.map(async (angle) => {
          try {
            const angleData = await anglesService.getAngle(angle.id);
            return {
              ...angle,
              attributes: {
                ...angle.attributes,
                resized_file_url: angleData.attributes?.file?.url || null,
              },
            };
          } catch (error) {
            console.error(
              `[UnifiedUploadModal] Failed to fetch angle ${angle.id}:`,
              error
            );
            return angle;
          }
        })
      );

      const imagesToPreload = updatedAngles
        .map((angle) => angle.attributes.resized_file_url)
        .filter((url): url is string => url !== null && url !== '');

      if (imagesToPreload.length > 0) {
        setPreloadProgress({ loaded: 0, total: imagesToPreload.length });

        await Promise.all(
          imagesToPreload.map(
            (url) =>
              new Promise<void>((resolve) => {
                const img = new Image();
                img.onload = () => {
                  setPreloadProgress((prev) => ({
                    ...prev,
                    loaded: prev.loaded + 1,
                  }));
                  resolve();
                };
                img.onerror = () => {
                  console.error(
                    `[UnifiedUploadModal] Failed to preload image: ${url}`
                  );
                  setPreloadProgress((prev) => ({
                    ...prev,
                    loaded: prev.loaded + 1,
                  }));
                  resolve();
                };
                img.src = url;
              })
          )
        );
      }

      setIsLoadingPreview(false);
      setHasCompletedPreview(true);
      unifiedProgress.complete();
      if (onComplete) {
        onComplete(updatedAngles);
      }
    } catch (error) {
      console.error(
        '[UnifiedUploadModal] Error during preview loading:',
        error
      );
      setIsLoadingPreview(false);
      setHasCompletedPreview(true);
      unifiedProgress.complete();
      if (onComplete) {
        const validAngles = angles.filter((angle) => {
          const validationItem = validationItems.find(
            (item) => item.id === angle.id
          );
          return validationItem?.status === 'valid';
        });
        onComplete(validAngles);
      }
    }
  }, [angles, validationItems, onComplete, unifiedProgress]);

  // Auto-trigger loading preview after successful validation
  useEffect(() => {
    if (
      allValidated &&
      !hasErrors &&
      phase === 'validating' &&
      !completedRef.current
    ) {
      handleLoadPreviewAndComplete();
    }
  }, [allValidated, hasErrors, phase, handleLoadPreviewAndComplete]);

  // Drop server-side rows for any angle that ended validation as Invalid.
  // Fire-and-forget: errors are logged and swallowed so they never block
  // the dismissal UX. The server's hourly cron is the safety net for
  // requests that fail (offline, tab killed mid-call). The corresponding
  // `angles/deleted` WS broadcast invalidates the angle-related TanStack
  // caches (incl. the `project-angles` root) via ENTITY_QUERY_KEYS in
  // realtime-sync.types.ts, so we don't manually invalidate here.
  const dismissInvalidAngles = useCallback(() => {
    const invalidIds = validationItems
      .filter(item => item.status === 'invalid')
      .map(item => item.id);

    if (invalidIds.length === 0) return;

    void anglesService.dismissInvalidAngles(invalidIds).catch(error => {
      console.warn('[UnifiedUploadModal] Failed to dismiss invalid angles', error);
    });
  }, [validationItems]);

  const handleClose = useCallback(() => {
    if (phase === 'complete' || hasErrors || showTimeout) {
      clearPolling();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      dismissInvalidAngles();
      onClose();
    }
  }, [phase, hasErrors, showTimeout, onClose, clearPolling, dismissInvalidAngles]);

  const handleCancel = useCallback(() => {
    clearPolling();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    dismissInvalidAngles();
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  }, [onCancel, onClose, clearPolling, dismissInvalidAngles]);

  const getPhaseLabel = () => {
    switch (phase) {
      case 'uploading':
        return t('phaseUploading');
      case 'validating':
        return isLoadingPreview
          ? t('phaseLoadingPreview')
          : t('phaseValidating');
      case 'complete':
        return t('phaseComplete');
      default:
        return t('phaseUploading');
    }
  };

  if (!isOpen) return null;

  const canClose =
    phase === 'complete' ||
    (phase === 'validating' && (allValidated || hasErrors));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-background rounded-lg p-6 max-w-md w-full min-h-[400px] flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-section-title">{t('title')}</h2>
          {canClose && (
            <button
              onClick={handleClose}
              className="text-muted-foreground hover:text-muted-foreground/80"
              aria-label={tCommon('close')}
            >
              <X className="w-5 w-5" />
            </button>
          )}
        </div>
        <div className="mb-4 pb-3 border-b border-border">
          <p className="text-list-item-subtitle text-muted-foreground">{getPhaseLabel()}</p>
        </div>

        {/* Upload Phase — real aggregated byte progress, no timer */}
        {phase === 'uploading' && uploadProgress && (
          <UploadingPhaseContent uploadProgress={uploadProgress} />
        )}

        {/* Validation Phase */}
        {phase === 'validating' && (
          <div className="flex-1 flex flex-col">
            {!isLoadingPreview && !hasCompletedPreview && (
              <>
                <ValidationItemsList items={validationItems} />

                {!allValidated && !hasErrors && unifiedProgress.isActive && (
                  <div className="space-y-2 mb-4">
                    <Progress
                      value={unifiedProgress.progress}
                      className="w-full"
                    />
                    <p className="text-progress-meta text-center text-muted-foreground">
                      {t('percentComplete', {
                        progress: unifiedProgress.progress,
                      })}
                    </p>
                  </div>
                )}

                {!allValidated && !hasErrors && (
                  <div className="text-center">
                    <span className="text-list-item-subtitle block">
                      {t('validatingMessage')}
                    </span>
                    <span className="text-card-meta text-muted-foreground mt-2 block">
                      {t('validatingTime')}
                    </span>
                  </div>
                )}

                {showTimeout && !hasErrors && !allValidated && (
                  <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={handleCancel}>
                      {t('cancel')}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowTimeout(false);
                        if (timeoutRef.current) {
                          clearTimeout(timeoutRef.current);
                        }
                        timeoutRef.current = setTimeout(() => {
                          setShowTimeout(true);
                        }, 30000);
                      }}
                    >
                      {t('continueWaiting')}
                    </Button>
                  </div>
                )}

                {hasErrors && (
                  <div className="flex justify-end gap-3 mt-6">
                    <Button variant="outline" onClick={handleCancel}>
                      {t('cancel')}
                    </Button>
                    <Button onClick={onRetry}>{t('uploadNewImages')}</Button>
                  </div>
                )}
              </>
            )}

            {(isLoadingPreview || hasCompletedPreview) && (
              <LoadingPreviewPhaseContent
                hasCompletedPreview={hasCompletedPreview}
                preloadProgress={preloadProgress}
              />
            )}
          </div>
        )}

        {/* Complete Phase */}
        {phase === 'complete' && (
          <CompletePhaseContent onClose={handleClose} />
        )}

        {/* Idle Phase */}
        {phase === 'idle' && angles.length === 0 && (
          <IdlePhaseContent onCancel={handleCancel} />
        )}
      </div>
    </div>
  );
}
